import type { StudioSystemType } from 'types/studio/items'
import { parseIntegrationJson } from 'util/misc'

import { getRoofName } from '../nativeMounting/utils'
import { getVanDerValkCorrugatedTypeForRoofType } from './pitchedCladding'
import {
  getDefaultPitchedCladdingOptions,
  isVanDerValkPitchedCladdingType,
  type PitchedCladdingOptions,
  type VanDerValkPitchedCladdingType,
} from './pitchedCladdingFieldConfig'

function clearSuitabilityCacheKeys(vdv: Record<string, unknown>): void {
  delete vdv.suitabilityResult
  delete vdv.selectedSuitability
  delete vdv.suitabilityModuleSpecs
  delete vdv.suitabilityCladdingType
  delete vdv.suitabilitySeamType
  delete vdv.suitabilityCladdingOptionsKey
  delete vdv.suitabilityGroupingVersion
  delete vdv.bom
}

function roofDerivedCorrugatedTypePartial(
  claddingType: VanDerValkPitchedCladdingType
): PitchedCladdingOptions {
  if (claddingType !== 'CORRUGATED') {
    return {}
  }
  const roofTypeName = getRoofName(window.projectForm?.getState?.()?.values?.roof_type) ?? null
  const corrugatedType = getVanDerValkCorrugatedTypeForRoofType(roofTypeName)
  return corrugatedType ? { corrugatedType } : {}
}

export function getVanDerValkPitchedCladdingOptionsFromSystem(
  system: StudioSystemType | null | undefined
): PitchedCladdingOptions | null {
  if (!system) {
    return null
  }
  const vdv = (parseIntegrationJson(system.integration_json) as { vanDerValk?: Record<string, unknown> } | null)
    ?.vanDerValk
  const raw = vdv?.pitchedCladdingOptions
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  return raw as PitchedCladdingOptions
}

export function getVanDerValkPitchedCladdingOptionsCladdingTypeFromSystem(
  system: StudioSystemType | null | undefined
): string | null {
  if (!system) {
    return null
  }
  const vdv = (parseIntegrationJson(system.integration_json) as { vanDerValk?: Record<string, unknown> } | null)
    ?.vanDerValk
  const raw = vdv?.pitchedCladdingOptionsCladdingType
  return typeof raw === 'string' ? raw : null
}

/**
 * Merge pitched cladding options into ``integration_json.vanDerValk`` and trigger mounting recalc.
 * Clears suitability cache when roof-derived cladding type changes (e.g. SLATE ↔ TILED) or when
 * ``corrugatedType`` changes under CORRUGATED so vendor suitability/BOM frames are not reused
 * across incompatible roofs.
 */
export function applyVanDerValkPitchedCladdingOptions(
  system: StudioSystemType,
  claddingType: VanDerValkPitchedCladdingType,
  partialOptions: PitchedCladdingOptions
): void {
  if (!system) {
    return
  }

  const raw = system.integration_json
  const base = (parseIntegrationJson(raw) as Record<string, unknown> | null) || {}
  const prevVdv = (base.vanDerValk as Record<string, unknown> | undefined) || {}

  const prevStoredType = prevVdv.pitchedCladdingOptionsCladdingType
  const prevOptions = (prevVdv.pitchedCladdingOptions as PitchedCladdingOptions | undefined) ?? {}
  const corrugatedHint =
    partialOptions.corrugatedType != null
      ? { corrugatedType: partialOptions.corrugatedType }
      : prevStoredType === claddingType && prevOptions.corrugatedType != null
        ? { corrugatedType: prevOptions.corrugatedType }
        : {}
  const baseOptions =
    prevStoredType === claddingType
      ? { ...prevOptions }
      : { ...getDefaultPitchedCladdingOptions(claddingType, corrugatedHint) }

  const merged: PitchedCladdingOptions = { ...baseOptions, ...partialOptions }
  if (claddingType === 'CORRUGATED' && String(merged.corrugatedType ?? '') === 'FIBER_CEMENT') {
    delete merged.claddingThickness
  }

  const prevCorrugated =
    prevStoredType === 'CORRUGATED' && prevOptions.corrugatedType != null
      ? String(prevOptions.corrugatedType)
      : null
  const nextCorrugated =
    claddingType === 'CORRUGATED' && merged.corrugatedType != null ? String(merged.corrugatedType) : null
  const corrugatedTypeChanged =
    claddingType === 'CORRUGATED' && prevCorrugated != null && nextCorrugated != null && prevCorrugated !== nextCorrugated
  const claddingTypeChanged =
    prevStoredType != null && String(prevStoredType) !== claddingType

  const vanDerValk: Record<string, unknown> = {
    ...prevVdv,
    pitchedCladdingOptions: merged,
    pitchedCladdingOptionsCladdingType: claddingType,
  }
  if (corrugatedTypeChanged || claddingTypeChanged) {
    clearSuitabilityCacheKeys(vanDerValk)
  }

  const integration_json = { ...base, vanDerValk } as unknown as typeof system.integration_json
  const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', integration_json, commandUUID))
  window.editor.signals.objectChanged.dispatch(system, 'integration_json')
  if (system.mounting === 'vanDerValk') {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }
}

/** Reset pitched cladding options when roof-derived cladding type changes. */
export function resetVanDerValkPitchedCladdingOptionsForType(
  system: StudioSystemType,
  claddingType: VanDerValkPitchedCladdingType
): void {
  const roofCorrugated = roofDerivedCorrugatedTypePartial(claddingType)
  applyVanDerValkPitchedCladdingOptions(system, claddingType, {
    ...getDefaultPitchedCladdingOptions(claddingType, roofCorrugated),
    ...roofCorrugated,
  })
}

/** Pitch / purlin / thickness / substructure defaults that differ by corrugatedType (Metal Tin = STEEL). */
function corrugatedTypeSpecificDefaults(
  claddingType: VanDerValkPitchedCladdingType,
  corrugatedType: string
): PitchedCladdingOptions {
  const defaults = getDefaultPitchedCladdingOptions(claddingType, { corrugatedType })
  if (corrugatedType === 'FIBER_CEMENT') {
    return {
      claddingPitch: defaults.claddingPitch ?? 1,
      purlinPitch: defaults.purlinPitch ?? 500,
      substructureMaterial: defaults.substructureMaterial ?? 'WOOD',
    }
  }
  const out: PitchedCladdingOptions = {}
  if (defaults.claddingPitch !== undefined) {
    out.claddingPitch = defaults.claddingPitch
  }
  if (defaults.purlinPitch !== undefined) {
    out.purlinPitch = defaults.purlinPitch
  }
  if (defaults.claddingThickness !== undefined) {
    out.claddingThickness = defaults.claddingThickness
  }
  if (defaults.substructureMaterial !== undefined) {
    out.substructureMaterial = defaults.substructureMaterial
  }
  return out
}

export function ensureVanDerValkPitchedCladdingOptionsForType(
  system: StudioSystemType,
  claddingType: VanDerValkPitchedCladdingType
): void {
  const storedType = getVanDerValkPitchedCladdingOptionsCladdingTypeFromSystem(system)
  if (storedType !== claddingType) {
    resetVanDerValkPitchedCladdingOptionsForType(system, claddingType)
    return
  }

  const current = getVanDerValkPitchedCladdingOptionsFromSystem(system) ?? {}
  const roofCorrugated = roofDerivedCorrugatedTypePartial(claddingType)
  const corrugatedType =
    roofCorrugated.corrugatedType != null
      ? String(roofCorrugated.corrugatedType)
      : current.corrugatedType != null
        ? String(current.corrugatedType)
        : null
  const defaults = getDefaultPitchedCladdingOptions(
    claddingType,
    corrugatedType != null ? { corrugatedType } : {}
  )
  const patch: PitchedCladdingOptions = {}
  for (const [key, value] of Object.entries(defaults)) {
    const optionKey = key as keyof PitchedCladdingOptions
    const raw = current[optionKey]
    if (raw === undefined || raw === '') {
      patch[optionKey] = value
    }
  }

  const corrugatedTypeChanged =
    roofCorrugated.corrugatedType != null &&
    String(current.corrugatedType ?? '') !== String(roofCorrugated.corrugatedType)
  if (corrugatedTypeChanged && roofCorrugated.corrugatedType != null) {
    patch.corrugatedType = roofCorrugated.corrugatedType
    Object.assign(patch, corrugatedTypeSpecificDefaults(claddingType, String(roofCorrugated.corrugatedType)))
  }

  if (Object.keys(patch).length > 0) {
    applyVanDerValkPitchedCladdingOptions(system, claddingType, patch)
  }
}

export { isVanDerValkPitchedCladdingType }
